import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertCircle, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useUsers } from '../hooks/use-users'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { ROLES } from '@/shared/constants/roles'

const ROLE_OPTIONS = Object.values(ROLES)

export function UsersPage() {
  const { user, hasRole } = useAuth()
  const { can } = usePermissions()
  const { t: tUsers } = useTranslation('users')
  const { t: tCommon } = useTranslation('common')
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | string>('all')
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'disabled'
  >('all')
  const [tenantIdInput, setTenantIdInput] = useState(
    user?.tenantId?.toString() ?? '',
  )

  const tenantId = useMemo(() => {
    if (user?.tenantId) return user.tenantId
    const parsed = Number(tenantIdInput)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [user?.tenantId, tenantIdInput])

  const { data, isLoading, error } = useUsers({
    search: search.trim() || undefined,
    role: roleFilter === 'all' ? undefined : roleFilter,
    status: statusFilter === 'all' ? undefined : statusFilter,
    tenantId,
    enabled: Boolean(tenantId),
  })

  const users = data?.items || []
  const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN)

  if (!can(ACTIONS.USER_MANAGE)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tUsers('permissions.deniedTitle')}</AlertTitle>
        <AlertDescription>
          {tUsers('permissions.deniedDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{tUsers('title')}</h1>
          <p className="text-muted-foreground mt-1">{tUsers('subtitle')}</p>
        </div>
        <Button asChild>
          <Link to="/users/new">
            <Plus className="mr-2 h-4 w-4" />
            {tUsers('create.title')}
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {isSuperAdmin && !user?.tenantId && (
          <div>
            <Label htmlFor="tenant-id">{tUsers('filters.tenantIdLabel')}</Label>
            <Input
              id="tenant-id"
              type="number"
              value={tenantIdInput}
              onChange={(event) => setTenantIdInput(event.target.value)}
              placeholder={tUsers('filters.tenantIdPlaceholder')}
              className="mt-1.5"
            />
          </div>
        )}
        <div>
          <Label htmlFor="user-search">{tUsers('filters.searchLabel')}</Label>
          <Input
            id="user-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tUsers('filters.searchPlaceholder')}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="role-filter">{tUsers('filters.roleLabel')}</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger id="role-filter" className="mt-1.5">
              <SelectValue placeholder={tUsers('filters.roleAll')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tUsers('filters.roleAll')}</SelectItem>
              {ROLE_OPTIONS.map((role) => (
                <SelectItem key={role} value={role}>
                  {role.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="status-filter">{tUsers('filters.statusLabel')}</Label>
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value as typeof statusFilter)
            }
          >
            <SelectTrigger id="status-filter" className="mt-1.5">
              <SelectValue placeholder={tUsers('filters.statusAll')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tUsers('filters.statusAll')}</SelectItem>
              <SelectItem value="active">{tUsers('status.active')}</SelectItem>
              <SelectItem value="disabled">
                {tUsers('status.disabled')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!tenantId && isSuperAdmin && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tUsers('tenant.requiredTitle')}</AlertTitle>
          <AlertDescription>
            {tUsers('tenant.requiredDescription')}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tCommon('error')}</AlertTitle>
          <AlertDescription>{tUsers('table.loadFailed')}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">
          {tUsers('table.loading')}
        </div>
      ) : users.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          {tUsers('table.empty')}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{tUsers('table.email')}</TableHead>
              <TableHead>{tUsers('table.roles')}</TableHead>
              <TableHead>{tUsers('table.status')}</TableHead>
              <TableHead>{tUsers('table.created')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">{entry.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {entry.roles.map((role) => (
                      <Badge key={role} variant="outline">
                        {role.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={entry.disabledAt ? 'secondary' : 'default'}>
                    {entry.disabledAt
                      ? tUsers('status.disabled')
                      : tUsers('status.active')}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {entry.createdAt
                    ? new Date(entry.createdAt).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/users/$id" params={{ id: String(entry.id) }}>
                      {tUsers('table.view')}
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
